import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, Users, IndianRupee, Activity, LogOut, Plus, Edit3,
  Power, PowerOff, RefreshCw, KeyRound, Shield, ClipboardList, Check,
  Upload, Trash2, Image as ImageIcon, Phone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { RenewalsPanel } from "@/components/RenewalsPanel";
import { Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/platform`;

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-800",
  trial: "bg-sky-100 text-sky-800",
  grace: "bg-amber-100 text-amber-800",
  expired: "bg-red-100 text-red-800",
  suspended: "bg-red-200 text-red-900",
  cancelled: "bg-slate-200 text-slate-700",
};

// Normalises FastAPI errors — 422 responses carry a list of validation
// dicts ({type, loc, msg, input, ctx}); anything else is a plain string.
function errMsg(e, fallback = "Failed") {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => `${(x.loc || []).slice(1).join(".") || "?"}: ${x.msg}`).join(" · ");
  if (typeof d === "string") return d;
  if (d && typeof d === "object") return JSON.stringify(d);
  return e?.message || fallback;
}

// Shared axios instance carrying the platform-admin JWT (falls back to cookie).
function useApi(token) {
  return axios.create({
    baseURL: API,
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const token = localStorage.getItem("dhoms_token") || "";
  const api = useApi(token);

  const [tab, setTab] = useState("hospitals"); // hospitals | plans | audits
  const [stats, setStats] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [defaultEnabled, setDefaultEnabled] = useState([]);
  const [audits, setAudits] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editHid, setEditHid] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, h, p, m, a] = await Promise.all([
        api.get("/stats"),
        api.get("/hospitals"),
        api.get("/plans"),
        api.get("/modules"),
        api.get("/audits", { params: { limit: 100 } }),
      ]);
      setStats(s.data);
      setHospitals(h.data);
      setPlans(p.data);
      setModules(m.data.catalog);
      setDefaultEnabled(m.data.default_enabled);
      setAudits(a.data);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load platform data"));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user?.role !== "platform_admin") {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-950 text-white">
        <div className="text-center space-y-4 p-8">
          <Shield className="w-12 h-12 mx-auto text-red-400"/>
          <h1 className="text-2xl font-semibold">Platform Access Only</h1>
          <p className="text-slate-400">Sign in with a Vyomra platform admin account.</p>
          <Button onClick={() => nav("/login")}>Back to login</Button>
        </div>
      </div>
    );
  }

  const doStatus = async (h, status) => {
    if (!window.confirm(`Set ${h.name} to ${status.toUpperCase()}?`)) return;
    try {
      await api.post(`/hospitals/${h.id}/status`, { status, reason: "Platform admin action" });
      toast.success(`${h.name} → ${status}`);
      load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const resetAdmin = async (h) => {
    const pw = window.prompt(`Set a new password for ${h.name}'s admin`);
    if (!pw || pw.length < 6) { toast.error("Min 6 chars"); return; }
    try {
      const { data } = await api.post(`/hospitals/${h.id}/reset-admin`, { new_password: pw });
      toast.success(`Reset done · admin: ${data.admin_email}`);
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/70 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-emerald-400"/>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Vyomra Platform</div>
              <div className="text-sm font-semibold">Super Admin Console</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 hidden md:inline">Signed in as <b className="text-slate-200">{user.email}</b></span>
            <Button size="sm" variant="outline" onClick={load} data-testid="platform-refresh" className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800">
              <RefreshCw className="w-4 h-4 mr-1"/> Refresh
            </Button>
            <Button size="sm" variant="outline" onClick={() => { logout(); nav("/login"); }} className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800">
              <LogOut className="w-4 h-4 mr-1"/> Sign out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Stats grid */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat label="Hospitals"  value={stats.hospitals} icon={Building2}/>
            <Stat label="Active"     value={stats.active}    icon={Power} tone="emerald"/>
            <Stat label="Trial"      value={stats.trial}     tone="sky"/>
            <Stat label="Suspended"  value={stats.suspended} tone="red"/>
            <Stat label="Users"      value={stats.users}     icon={Users}/>
            <Stat label="MRR"        value={`₹${stats.mrr}`} icon={IndianRupee} tone="emerald"/>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-800">
          {[
            ["hospitals", "Hospitals", Building2],
            ["plans", "Subscription Plans", IndianRupee],
            ["renewals", "Renewal Payments", Wallet],
            ["audits", "Audit Log", ClipboardList],
          ].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`platform-tab-${k}`}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px ${tab === k ? "text-white border-emerald-400" : "text-slate-400 border-transparent hover:text-slate-200"}`}>
              <Icon className="w-4 h-4"/> {label}
            </button>
          ))}
        </div>

        {tab === "hospitals" && (
          <HospitalsPanel
            hospitals={hospitals}
            plans={plans}
            modules={modules}
            defaultEnabled={defaultEnabled}
            api={api}
            onReload={load}
            onCreate={() => setCreateOpen(true)}
            onEdit={setEditHid}
            onSuspend={(h) => doStatus(h, "suspended")}
            onReactivate={(h) => doStatus(h, "active")}
            onResetAdmin={resetAdmin}
          />
        )}
        {tab === "plans" && (
          <PlansPanel plans={plans} modules={modules} api={api} onReload={load}/>
        )}
        {tab === "renewals" && (
          <RenewalsPanel api={api} onReload={load}/>
        )}
        {tab === "audits" && (
          <AuditPanel audits={audits}/>
        )}
      </div>

      {createOpen && (
        <CreateHospitalDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          plans={plans}
          modules={modules}
          defaultEnabled={defaultEnabled}
          api={api}
          onCreated={() => { setCreateOpen(false); load(); }}
        />
      )}
      {editHid && (
        <EditHospitalDialog
          hid={editHid}
          onClose={() => setEditHid(null)}
          plans={plans}
          modules={modules}
          api={api}
          onSaved={() => { setEditHid(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── Stat card ──────────────────────────────────────────────────────────
function Stat({ label, value, icon: Icon, tone = "slate" }) {
  const toneMap = {
    slate: "text-slate-200",
    emerald: "text-emerald-400",
    sky: "text-sky-400",
    red: "text-red-400",
  };
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg px-4 py-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
        {Icon && <Icon className="w-3 h-3"/>} {label}
      </div>
      <div className={`text-2xl font-mono font-bold mt-1 ${toneMap[tone]}`}>{value}</div>
    </div>
  );
}

// ─── Hospitals panel ────────────────────────────────────────────────────
function HospitalsPanel({ hospitals, plans, modules, defaultEnabled, api, onReload, onCreate, onEdit, onSuspend, onReactivate, onResetAdmin }) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-400">{hospitals.length} hospital(s) on the platform</div>
        <Button onClick={onCreate} data-testid="platform-hospital-new" className="bg-emerald-600 hover:bg-emerald-500 text-white">
          <Plus className="w-4 h-4 mr-1"/> New Hospital
        </Button>
      </div>
      <div className="border border-slate-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-slate-400">
            <tr>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Hospital</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Plan</th>
              <th className="text-right px-3 py-2">Users</th>
              <th className="text-right px-3 py-2">Patients</th>
              <th className="text-right px-3 py-2">Modules</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {hospitals.map((h) => (
              <tr key={h.id} className="hover:bg-slate-900/50">
                <td className="px-3 py-2 font-mono text-xs text-emerald-300">{h.code}</td>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-100">{h.name}</div>
                  <div className="text-[11px] text-slate-500">{[h.city, h.state].filter(Boolean).join(", ")}</div>
                </td>
                <td className="px-3 py-2">
                  <span data-testid={`platform-status-${h.code}`} className={`text-[10px] uppercase px-2 py-0.5 rounded-sm font-semibold ${STATUS_COLORS[h.status] || "bg-slate-200 text-slate-800"}`}>
                    {h.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">
                  <div>{h.subscription?.plan_name || "—"}</div>
                  <div className="text-slate-500 font-mono">₹{h.subscription?.price_override ?? h.subscription?.plan_price ?? 0}/{h.subscription?.billing_cycle || "—"}</div>
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">{h.counts?.users ?? 0}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{h.counts?.patients ?? 0}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{(h.enabled_modules || []).length}/{modules.length}</td>
                <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => onEdit(h.id)} data-testid={`platform-edit-${h.code}`} className="h-7 px-2 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800">
                    <Edit3 className="w-3 h-3"/>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onResetAdmin(h)} data-testid={`platform-reset-${h.code}`} className="h-7 px-2 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800">
                    <KeyRound className="w-3 h-3"/>
                  </Button>
                  {h.status === "suspended" || h.status === "expired" ? (
                    <Button size="sm" onClick={() => onReactivate(h)} data-testid={`platform-activate-${h.code}`} className="h-7 px-2 bg-emerald-600 hover:bg-emerald-500 text-white">
                      <Power className="w-3 h-3"/>
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => onSuspend(h)} data-testid={`platform-suspend-${h.code}`} className="h-7 px-2 bg-red-600 hover:bg-red-500 text-white">
                      <PowerOff className="w-3 h-3"/>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {hospitals.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No hospitals yet — click <b>New Hospital</b>.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Plans panel ────────────────────────────────────────────────────────
function PlansPanel({ plans, modules, api, onReload }) {
  const [editing, setEditing] = useState(null); // plan being edited
  const [creating, setCreating] = useState(false);

  const openNew = () => setEditing({
    name: "", price: 0, billing_cycle: "monthly",
    modules: [], user_limit: 0, doctor_limit: 0, patient_limit: 0, storage_gb: 0,
    features: [], description: "", active: true,
  });

  const save = async () => {
    try {
      if (editing.id) await api.put(`/plans/${editing.id}`, editing);
      else await api.post("/plans", editing);
      toast.success("Saved");
      setEditing(null); setCreating(false); onReload();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between">
        <div className="text-sm text-slate-400">{plans.length} plan(s)</div>
        <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="platform-plan-new">
          <Plus className="w-4 h-4 mr-1"/> New Plan
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {plans.map((p) => (
          <div key={p.id} className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-lg font-semibold">{p.name}</div>
                <div className="text-2xl font-mono text-emerald-400 mt-1">₹{p.price}<span className="text-xs text-slate-500 ml-1">/{p.billing_cycle}</span></div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setEditing(p)} className="h-7 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" data-testid={`platform-plan-edit-${p.name}`}>
                <Edit3 className="w-3 h-3"/>
              </Button>
            </div>
            <div className="mt-3 space-y-1 text-xs text-slate-400">
              <div>Modules: <span className="text-slate-200">{p.modules.length}</span></div>
              <div>Users: <span className="text-slate-200">{p.user_limit || "∞"}</span> · Doctors: <span className="text-slate-200">{p.doctor_limit || "∞"}</span></div>
              <div>Storage: <span className="text-slate-200">{p.storage_gb || "∞"}</span> GB</div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-2xl bg-slate-900 text-slate-100 border-slate-800">
            <DialogHeader><DialogTitle>{editing.id ? "Edit Plan" : "New Plan"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-400">Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-slate-800 border-slate-700"/></div>
                <div><Label className="text-slate-400">Price (₹)</Label><Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: parseFloat(e.target.value) || 0 })} className="bg-slate-800 border-slate-700"/></div>
                <div><Label className="text-slate-400">Billing Cycle</Label>
                  <select value={editing.billing_cycle} onChange={(e) => setEditing({ ...editing, billing_cycle: e.target.value })} className="w-full h-10 bg-slate-800 border border-slate-700 rounded-md px-3 text-sm">
                    {["monthly", "quarterly", "yearly", "custom"].map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div><Label className="text-slate-400">User limit (0 = ∞)</Label><Input type="number" value={editing.user_limit} onChange={(e) => setEditing({ ...editing, user_limit: parseInt(e.target.value) || 0 })} className="bg-slate-800 border-slate-700"/></div>
                <div><Label className="text-slate-400">Doctor limit</Label><Input type="number" value={editing.doctor_limit} onChange={(e) => setEditing({ ...editing, doctor_limit: parseInt(e.target.value) || 0 })} className="bg-slate-800 border-slate-700"/></div>
                <div><Label className="text-slate-400">Storage GB</Label><Input type="number" value={editing.storage_gb} onChange={(e) => setEditing({ ...editing, storage_gb: parseInt(e.target.value) || 0 })} className="bg-slate-800 border-slate-700"/></div>
              </div>
              <ModulesGrid modules={modules} selected={editing.modules} onToggle={(k) => setEditing({ ...editing, modules: editing.modules.includes(k) ? editing.modules.filter((m) => m !== k) : [...editing.modules, k] })}/>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)} className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800">Cancel</Button>
              <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="platform-plan-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ─── Modules grid ────────────────────────────────────────────────────────
function ModulesGrid({ modules, selected, onToggle }) {
  return (
    <div>
      <Label className="text-slate-400">Modules Included <span className="text-[10px] text-slate-500 ml-1">({selected.length}/{modules.length})</span></Label>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 mt-1 max-h-56 overflow-y-auto p-2 border border-slate-700 rounded-md bg-slate-950">
        {modules.map((m) => (
          <button key={m.key} type="button" onClick={() => onToggle(m.key)} data-testid={`platform-mod-${m.key}`}
            className={`text-[11px] px-2 py-1 border rounded-sm text-left transition ${selected.includes(m.key) ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
            {m.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Audits panel ────────────────────────────────────────────────────────
function AuditPanel({ audits }) {
  return (
    <div className="border border-slate-800 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-900 text-slate-400">
          <tr>
            <th className="text-left px-3 py-2">When</th>
            <th className="text-left px-3 py-2">Actor</th>
            <th className="text-left px-3 py-2">Action</th>
            <th className="text-left px-3 py-2">Target</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {audits.map((a) => (
            <tr key={a.id}>
              <td className="px-3 py-2 text-xs font-mono text-slate-400">{new Date(a.created_at).toLocaleString()}</td>
              <td className="px-3 py-2 text-xs">{a.actor_email}</td>
              <td className="px-3 py-2 text-xs font-mono text-emerald-300">{a.action}</td>
              <td className="px-3 py-2 text-xs font-mono text-slate-400">{a.target}</td>
            </tr>
          ))}
          {audits.length === 0 && (
            <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-500">No platform activity yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Onboarding Wizard (4 steps + success) ────────────────────────────────
function CreateHospitalDialog({ open, onClose, plans, modules, defaultEnabled, api, onCreated }) {
  const [step, setStep] = useState(0);   // 0 details · 1 plan · 2 modules · 3 admin · 4 success
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    code: "", name: "",
    address: "", city: "", state: "", pin: "", phone: "", email: "", website: "", gst: "",
    uhid_prefix: "UH",
    plan_id: "", enabled_modules: defaultEnabled,
    admin_name: "", admin_email: "", admin_password: "",
    subscription_end: "", price_override: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.plan_id) delete payload.plan_id;
      if (payload.price_override === "") delete payload.price_override;
      else payload.price_override = parseFloat(payload.price_override);
      if (!payload.subscription_end) delete payload.subscription_end;
      if (!payload.email) delete payload.email;
      const { data } = await api.post("/hospitals", payload);
      setResult(data);
      setStep(4);
      toast.success(`Created ${data.hospital.name}`);
    } catch (e) {
      toast.error(errMsg(e, "Failed"));
    } finally { setBusy(false); }
  };

  const stepErr = () => {
    if (step === 0) {
      if (!form.code.trim() || !form.name.trim()) return "Code and name are required";
    }
    if (step === 3) {
      if (!form.admin_name.trim() || !form.admin_email.trim() || form.admin_password.length < 6) return "Admin name, valid email and a 6+ char password required";
    }
    return null;
  };

  const next = () => {
    const err = stepErr();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(4, s + 1));
  };

  const stepDefs = [
    { title: "Hospital Details", sub: "Legal identity and address" },
    { title: "Subscription Plan", sub: "Pick a plan or leave as trial" },
    { title: "Modules", sub: "Which features this hospital can access" },
    { title: "First Admin", sub: "The account that will manage this hospital" },
    { title: "All Set!", sub: "Onboarding complete" },
  ];

  const chosenPlan = plans.find((p) => p.id === form.plan_id);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-slate-900 text-slate-100 border-slate-800">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>{stepDefs[step].title}</span>
            {step < 4 && <span className="text-xs font-normal text-slate-500">Step {step + 1} of 4</span>}
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-0.5">{stepDefs[step].sub}</p>
        </DialogHeader>

        {/* Progress bar */}
        {step < 4 && (
          <div className="flex gap-1 mb-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-emerald-500" : "bg-slate-800"}`}/>
            ))}
          </div>
        )}

        {step === 0 && (
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-400">Code (unique)</Label><Input data-testid="wizard-code" value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="TESTB" className="bg-slate-800 border-slate-700 font-mono"/></div>
            <div><Label className="text-slate-400">UHID Prefix</Label><Input value={form.uhid_prefix} onChange={(e) => set("uhid_prefix", e.target.value.toUpperCase())} placeholder="TB" className="bg-slate-800 border-slate-700 font-mono"/></div>
            <div className="col-span-2"><Label className="text-slate-400">Hospital Name</Label><Input data-testid="wizard-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-slate-800 border-slate-700"/></div>
            <div className="col-span-2"><Label className="text-slate-400">Address</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">City</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">State</Label><Input value={form.state} onChange={(e) => set("state", e.target.value)} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">PIN</Label><Input value={form.pin} onChange={(e) => set("pin", e.target.value)} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">GST (optional)</Label><Input value={form.gst} onChange={(e) => set("gst", e.target.value)} className="bg-slate-800 border-slate-700 font-mono"/></div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button onClick={() => set("plan_id", "")}
                className={`text-left p-4 border rounded-lg transition ${form.plan_id === "" ? "border-emerald-500 bg-emerald-500/10" : "border-slate-700 hover:border-slate-500"}`}>
                <div className="text-xs uppercase tracking-widest text-sky-300 font-semibold">Trial</div>
                <div className="text-lg font-semibold mt-1">Free 14-day trial</div>
                <div className="text-xs text-slate-400 mt-2">Skip billing for now · configure a plan later</div>
              </button>
              {plans.map((p) => (
                <button key={p.id} onClick={() => set("plan_id", p.id)}
                  className={`text-left p-4 border rounded-lg transition ${form.plan_id === p.id ? "border-emerald-500 bg-emerald-500/10" : "border-slate-700 hover:border-slate-500"}`}>
                  <div className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">{p.name}</div>
                  <div className="text-2xl font-mono font-bold mt-1">₹{p.price}<span className="text-xs text-slate-500 ml-1">/{p.billing_cycle}</span></div>
                  <div className="text-xs text-slate-400 mt-2">{p.modules.length} modules · {p.user_limit || "∞"} users</div>
                </button>
              ))}
            </div>
            {chosenPlan && (
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-800">
                <div><Label className="text-slate-400">Custom Price Override (₹)</Label><Input type="number" value={form.price_override} onChange={(e) => set("price_override", e.target.value)} placeholder={String(chosenPlan.price)} className="bg-slate-800 border-slate-700"/></div>
                <div><Label className="text-slate-400">Subscription End</Label><Input type="datetime-local" value={form.subscription_end} onChange={(e) => set("subscription_end", e.target.value)} className="bg-slate-800 border-slate-700"/></div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Pick every module this hospital should access.</span>
              <button className="text-emerald-400 hover:underline" onClick={() => set("enabled_modules", modules.map((m) => m.key))}>Select all</button>
            </div>
            <ModulesGrid modules={modules} selected={form.enabled_modules} onToggle={(k) => set("enabled_modules", form.enabled_modules.includes(k) ? form.enabled_modules.filter((m) => m !== k) : [...form.enabled_modules, k])}/>
          </div>
        )}

        {step === 3 && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-slate-400">Admin Full Name</Label><Input data-testid="wizard-admin-name" value={form.admin_name} onChange={(e) => set("admin_name", e.target.value)} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">Admin Email</Label><Input data-testid="wizard-admin-email" type="email" value={form.admin_email} onChange={(e) => set("admin_email", e.target.value)} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">Password (min 6)</Label><Input data-testid="wizard-admin-pw" type="text" value={form.admin_password} onChange={(e) => set("admin_password", e.target.value)} className="bg-slate-800 border-slate-700 font-mono"/></div>
            <div className="col-span-2 text-[11px] text-slate-500 bg-slate-950 border border-slate-800 rounded-md p-3">
              The password is shown in plain text on the final step so you can share it with the hospital.
              Ask the admin to change it after their first login.
            </div>
          </div>
        )}

        {step === 4 && result && (
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 grid place-items-center mx-auto">
                <Check className="w-8 h-8 text-emerald-400"/>
              </div>
              <h2 className="text-2xl font-semibold mt-3">{result.hospital.name} is live</h2>
              <p className="text-sm text-slate-400">Share these credentials with the hospital admin.</p>
            </div>
            <div className="bg-slate-950 border border-emerald-500/40 rounded-lg p-4 font-mono text-sm space-y-1">
              <Row lbl="Hospital Code"   val={result.hospital.code}/>
              <Row lbl="Hospital ID"     val={result.hospital.id}/>
              <Row lbl="Admin Email"     val={result.admin.email}/>
              <Row lbl="Admin Password"  val={form.admin_password}/>
              <Row lbl="Enabled Modules" val={`${result.hospital.enabled_modules.length}/${modules.length}`}/>
              <Row lbl="Status"          val={result.hospital.status}/>
            </div>
            <div className="text-[11px] text-slate-500 space-y-1">
              <div>1. Send the admin the email above and this password.</div>
              <div>2. They can log in at <span className="text-emerald-300">{window.location.origin}/login</span> — they will land on their hospital dashboard automatically.</div>
              <div>3. First task in-app: <b className="text-slate-300">upload a logo</b>, add doctors, set charges.</div>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 4 ? (
            <Button onClick={() => { onCreated(); }} className="bg-emerald-600 hover:bg-emerald-500 text-white w-full" data-testid="wizard-done">
              Done
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={step === 0 ? onClose : () => setStep((s) => s - 1)} className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800">
                {step === 0 ? "Cancel" : "Back"}
              </Button>
              {step < 3 ? (
                <Button onClick={next} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="wizard-next">Next →</Button>
              ) : (
                <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="wizard-submit">
                  {busy ? "Creating…" : "Create Hospital"}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ lbl, val }) {
  return (
    <div className="flex justify-between items-center gap-3">
      <span className="text-[10px] uppercase tracking-widest text-slate-500">{lbl}</span>
      <span className="text-slate-100 select-all">{val}</span>
    </div>
  );
}

// ─── Branding & Letterhead editor ───────────────────────────────────────
function BrandingEditor({ hid, branding, setBranding, onSave, onUpload, onRemoveLogo, logoBust, apiOrigin }) {
  const set = (k, v) => setBranding({ ...branding, [k]: v });
  const phones = branding.phones && branding.phones.length ? branding.phones : [""];
  const setPhone = (i, v) => {
    const arr = [...phones];
    arr[i] = v;
    set("phones", arr);
  };
  const addPhone = () => set("phones", [...phones, ""]);
  const removePhone = (i) => set("phones", phones.filter((_, x) => x !== i));

  const backend = apiOrigin || process.env.REACT_APP_BACKEND_URL || "";
  const logoSrc = `${backend}/api/branding/logo?hospital_id=${hid}&v=${logoBust}`;

  return (
    <div className="space-y-4">
      {/* Live letterhead preview */}
      <div className="border border-slate-700 rounded-lg bg-white p-4 text-slate-900">
        <div className="flex items-center gap-4">
          {branding.has_logo ? (
            <img src={logoSrc} alt={branding.name} className="h-16 object-contain"/>
          ) : (
            <div className="h-16 w-16 rounded-lg border-2 border-dashed border-slate-300 grid place-items-center text-slate-400">
              <ImageIcon className="w-6 h-6"/>
            </div>
          )}
          <div className="flex-1 text-center">
            <div className="font-display font-semibold text-xl leading-tight">{branding.name || "—"}</div>
            <div className="text-[10px] tracking-widest uppercase text-slate-600">{branding.tagline}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{branding.address}</div>
            {(phones.filter(Boolean).length > 0 || branding.email) && (
              <div className="text-[10px] text-slate-500 mt-0.5 flex items-center justify-center gap-2 flex-wrap">
                {phones.filter(Boolean).map((p, i) => <span key={`ph-${p}-${i}`} className="flex items-center gap-1"><Phone className="w-2.5 h-2.5"/> {p}</span>)}
                {branding.email && <span>· {branding.email}</span>}
                {branding.website && <span>· {branding.website}</span>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Logo upload */}
      <div className="flex items-center gap-3 border border-slate-800 rounded-lg p-3">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Hospital Logo</div>
          <div className="text-[11px] text-slate-500 mt-0.5">PNG or JPG · max 4 MB · recommended 400×400 with transparent background</div>
        </div>
        <label className="cursor-pointer bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-2 rounded-md text-xs font-medium flex items-center gap-1" data-testid="branding-upload">
          <Upload className="w-3.5 h-3.5"/> Upload
          <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])}/>
        </label>
        {branding.has_logo && (
          <Button variant="outline" onClick={onRemoveLogo} className="h-9 bg-transparent border-red-800 text-red-300 hover:bg-red-950" data-testid="branding-logo-remove">
            <Trash2 className="w-3.5 h-3.5"/>
          </Button>
        )}
      </div>

      {/* Identity form */}
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label className="text-slate-400">Legal Name (printed on all documents)</Label><Input data-testid="branding-name" value={branding.name || ""} onChange={(e) => set("name", e.target.value)} className="bg-slate-800 border-slate-700"/></div>
        <div><Label className="text-slate-400">Short Name (sidebar & receipts)</Label><Input value={branding.short_name || ""} onChange={(e) => set("short_name", e.target.value)} className="bg-slate-800 border-slate-700"/></div>
        <div><Label className="text-slate-400">Tagline</Label><Input value={branding.tagline || ""} onChange={(e) => set("tagline", e.target.value)} placeholder="MULTI SPECIALITY HOSPITAL" className="bg-slate-800 border-slate-700"/></div>
        <div className="col-span-2"><Label className="text-slate-400">Full Address (on letterhead)</Label><Input value={branding.address || ""} onChange={(e) => set("address", e.target.value)} className="bg-slate-800 border-slate-700"/></div>
        <div><Label className="text-slate-400">Email</Label><Input value={branding.email || ""} onChange={(e) => set("email", e.target.value)} className="bg-slate-800 border-slate-700"/></div>
        <div><Label className="text-slate-400">Website</Label><Input value={branding.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="hospitalname.com" className="bg-slate-800 border-slate-700"/></div>
        <div className="col-span-2">
          <Label className="text-slate-400">Phones <span className="text-[10px] text-slate-500 ml-1">(shown on every printout)</span></Label>
          <div className="space-y-1 mt-1">
            {phones.map((p, i) => (
              <div key={i} className="flex gap-2">
                <Input value={p} onChange={(e) => setPhone(i, e.target.value)} placeholder={i === 0 ? "+91 9274682922" : "Additional line"} className="bg-slate-800 border-slate-700 font-mono"/>
                {phones.length > 1 && (
                  <Button variant="outline" onClick={() => removePhone(i)} className="h-9 bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800">
                    <Trash2 className="w-3.5 h-3.5"/>
                  </Button>
                )}
              </div>
            ))}
            <button onClick={addPhone} className="text-emerald-400 text-xs hover:underline">+ Add another phone</button>
          </div>
        </div>
        <label className="col-span-2 flex items-center gap-2 text-xs text-slate-300 mt-2">
          <input type="checkbox" checked={!!branding.abdm_enabled} onChange={(e) => set("abdm_enabled", e.target.checked)}/>
          <span>Show <b>ABDM Health ID</b> module in this hospital's sidebar</span>
        </label>
      </div>

      <div className="flex justify-end pt-2 border-t border-slate-800">
        <Button onClick={onSave} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="branding-save">
          Save Branding
        </Button>
      </div>
    </div>
  );
}

// ─── Edit Hospital dialog ────────────────────────────────────────────────
function EditHospitalDialog({ hid, onClose, plans, modules, api, onSaved }) {
  const [h, setH] = useState(null);
  const [tab, setTab] = useState("details");
  const [branding, setBranding] = useState(null);
  const [logoBust, setLogoBust] = useState(Date.now());

  useEffect(() => { api.get(`/hospitals/${hid}`).then((r) => setH(r.data)); /* eslint-disable-next-line */ }, [hid]);
  useEffect(() => {
    if (tab === "branding" && !branding) {
      api.get(`/hospitals/${hid}/branding`).then((r) => setBranding(r.data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [tab, hid]);

  if (!h) return null;

  const saveDetails = async () => {
    try {
      await api.put(`/hospitals/${hid}`, {
        name: h.name, address: h.address, city: h.city, state: h.state, pin: h.pin,
        phone: h.phone, email: h.email, website: h.website, gst: h.gst, uhid_prefix: h.uhid_prefix,
        custom_domain: (h.custom_domain || "").trim().toLowerCase(),
      });
      toast.success("Details saved"); onSaved();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const saveModules = async () => {
    try {
      await api.post(`/hospitals/${hid}/modules`, { enabled_modules: h.enabled_modules });
      toast.success("Modules updated"); onSaved();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const saveSub = async () => {
    try {
      const sub = h.subscription || {};
      await api.post(`/hospitals/${hid}/subscription`, {
        plan_id: sub.plan_id || null,
        subscription_start: sub.subscription_start || null,
        subscription_end: sub.subscription_end || null,
        price_override: sub.price_override !== undefined && sub.price_override !== "" ? parseFloat(sub.price_override) : null,
        billing_cycle: sub.billing_cycle || null,
      });
      toast.success("Subscription updated"); onSaved();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const saveBranding = async () => {
    try {
      const payload = {
        name: branding.name || h.name,
        short_name: branding.short_name || "",
        tagline: branding.tagline || "",
        address: branding.address || "",
        phones: (branding.phones || []).filter((p) => (p || "").trim()),
        email: branding.email || "",
        website: branding.website || "",
        abdm_enabled: !!branding.abdm_enabled,
      };
      await api.put(`/hospitals/${hid}/branding`, payload);
      toast.success("Branding saved");
      setLogoBust(Date.now());
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("File must be an image"); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error("Logo must be under 4 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/hospitals/${hid}/branding/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      setLogoBust(Date.now());
      // Refresh has_logo in state
      const { data } = await api.get(`/hospitals/${hid}/branding`);
      setBranding(data);
    } catch (e) { toast.error(errMsg(e, "Upload failed")); }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the current logo?")) return;
    try {
      await api.delete(`/hospitals/${hid}/branding/logo`);
      toast.success("Logo removed");
      setLogoBust(Date.now());
      const { data } = await api.get(`/hospitals/${hid}/branding`);
      setBranding(data);
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-slate-900 text-slate-100 border-slate-800">
        <DialogHeader>
          <DialogTitle>{h.name} <span className="text-xs text-slate-400 font-mono ml-2">{h.code}</span></DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 border-b border-slate-800 -mt-2 overflow-x-auto">
          {[
            ["details","Details"],
            ["branding","Branding & Letterhead"],
            ["modules",`Modules (${h.enabled_modules?.length || 0}/${modules.length})`],
            ["subscription","Subscription"],
          ].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`edit-tab-${k}`}
              className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap ${tab === k ? "text-white border-emerald-400" : "text-slate-400 border-transparent hover:text-slate-200"}`}>{label}</button>
          ))}
        </div>
        {tab === "details" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-slate-400">Name</Label><Input value={h.name || ""} onChange={(e) => setH({ ...h, name: e.target.value })} className="bg-slate-800 border-slate-700"/></div>
            <div className="col-span-2"><Label className="text-slate-400">Address</Label><Input value={h.address || ""} onChange={(e) => setH({ ...h, address: e.target.value })} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">City</Label><Input value={h.city || ""} onChange={(e) => setH({ ...h, city: e.target.value })} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">State</Label><Input value={h.state || ""} onChange={(e) => setH({ ...h, state: e.target.value })} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">PIN</Label><Input value={h.pin || ""} onChange={(e) => setH({ ...h, pin: e.target.value })} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">Phone</Label><Input value={h.phone || ""} onChange={(e) => setH({ ...h, phone: e.target.value })} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">Email</Label><Input value={h.email || ""} onChange={(e) => setH({ ...h, email: e.target.value })} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">GST</Label><Input value={h.gst || ""} onChange={(e) => setH({ ...h, gst: e.target.value })} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">UHID Prefix</Label><Input value={h.uhid_prefix || ""} onChange={(e) => setH({ ...h, uhid_prefix: e.target.value.toUpperCase() })} className="bg-slate-800 border-slate-700 font-mono"/></div>
            <div className="col-span-2">
              <Label className="text-slate-400">Custom Domain <span className="text-[10px] text-slate-500 ml-1">(e.g. <span className="font-mono">portal.divinehospitalgujarat.com</span> · leave blank to use the platform subdomain <span className="font-mono">{h.code?.toLowerCase()}.d-homs.online</span>)</span></Label>
              <Input value={h.custom_domain || ""} onChange={(e) => setH({ ...h, custom_domain: e.target.value })} placeholder="portal.divinehospitalgujarat.com" className="bg-slate-800 border-slate-700 font-mono"/>
            </div>
            <div className="col-span-2 flex justify-end">
              <Button onClick={saveDetails} className="bg-emerald-600 hover:bg-emerald-500 text-white">Save Details</Button>
            </div>
          </div>
        )}
        {tab === "branding" && (
          !branding ? (
            <div className="py-8 text-center text-slate-500 text-sm">Loading branding…</div>
          ) : (
            <BrandingEditor
              hid={hid}
              branding={branding}
              setBranding={setBranding}
              onSave={saveBranding}
              onUpload={uploadLogo}
              onRemoveLogo={removeLogo}
              logoBust={logoBust}
              apiOrigin={api.defaults?.baseURL?.replace(/\/api\/platform$/, "")}
            />
          )
        )}
        {tab === "modules" && (
          <div className="space-y-3">
            <ModulesGrid modules={modules} selected={h.enabled_modules || []} onToggle={(k) => {
              const arr = h.enabled_modules || [];
              setH({ ...h, enabled_modules: arr.includes(k) ? arr.filter((m) => m !== k) : [...arr, k] });
            }}/>
            <div className="flex justify-end">
              <Button onClick={saveModules} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="platform-save-modules">Save Modules</Button>
            </div>
          </div>
        )}
        {tab === "subscription" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-slate-400">Plan</Label>
              <select value={h.subscription?.plan_id || ""} onChange={(e) => setH({ ...h, subscription: { ...(h.subscription || {}), plan_id: e.target.value } })} className="w-full h-10 bg-slate-800 border border-slate-700 rounded-md px-3 text-sm">
                <option value="">— None —</option>
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name} · ₹{p.price}/{p.billing_cycle}</option>)}
              </select>
            </div>
            <div><Label className="text-slate-400">Billing Cycle</Label>
              <select value={h.subscription?.billing_cycle || "monthly"} onChange={(e) => setH({ ...h, subscription: { ...(h.subscription || {}), billing_cycle: e.target.value } })} className="w-full h-10 bg-slate-800 border border-slate-700 rounded-md px-3 text-sm">
                {["monthly", "quarterly", "yearly", "custom"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><Label className="text-slate-400">Custom Price Override (₹)</Label><Input type="number" value={h.subscription?.price_override ?? ""} onChange={(e) => setH({ ...h, subscription: { ...(h.subscription || {}), price_override: e.target.value } })} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">Subscription Start</Label><Input type="datetime-local" value={(h.subscription?.subscription_start || "").slice(0, 16)} onChange={(e) => setH({ ...h, subscription: { ...(h.subscription || {}), subscription_start: e.target.value } })} className="bg-slate-800 border-slate-700"/></div>
            <div><Label className="text-slate-400">Subscription End</Label><Input type="datetime-local" value={(h.subscription?.subscription_end || "").slice(0, 16)} onChange={(e) => setH({ ...h, subscription: { ...(h.subscription || {}), subscription_end: e.target.value } })} className="bg-slate-800 border-slate-700"/></div>
            <div className="col-span-2 flex justify-end">
              <Button onClick={saveSub} className="bg-emerald-600 hover:bg-emerald-500 text-white">Save Subscription</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
